import React, { useEffect } from 'react';
import { SEO } from '../components/SEO';
import { SignIn } from '@clerk/react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { PublicHeader } from '../components/shell/PublicHeader';
import { PublicFooter } from '../components/shell/PublicFooter';
import { useCurrentUser } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { SHOULD_USE_MOCKS } from '../lib/clerk';
import { Users, Lock, Sparkles, ShieldAlert } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { cn } from '../lib/utils';

const appearance = {
  elements: {
    formButtonPrimary: 'bg-navy-900 hover:bg-navy-800 text-sm normal-case dark:bg-brand-primary dark:text-white',
    card: 'shadow-none border-none bg-transparent',
    headerTitle: 'text-navy-900 dark:text-white font-display',
    headerSubtitle: 'text-gray-500 dark:text-gray-400',
    socialButtonsBlockButton: 'border-gray-300 hover:bg-gray-50 text-gray-700 dark:bg-slate-800 dark:border-slate-700 dark:text-white dark:hover:bg-slate-700',
    socialButtonsBlockButtonText: 'dark:text-white font-medium',
    dividerText: 'dark:text-gray-400',
    dividerLine: 'dark:bg-slate-700',
    formFieldLabel: 'dark:text-gray-300',
    formFieldInput: 'dark:bg-slate-800 dark:border-slate-700 dark:text-white',
    footerActionText: 'dark:text-gray-400',
    footerActionLink: 'text-blue-600 hover:text-blue-700 font-medium dark:text-blue-400 dark:hover:text-blue-300',
    identityPreviewText: 'dark:text-white',
    identityPreviewEditButton: 'dark:text-blue-400 hover:dark:text-blue-300',
  },
};

export const SignInPage = () => {
  const { isSignedIn, isLoading: isAuthLoading, isMaintenanceMode, loginAs, hardReset } = useCurrentUser();
  const { activeBrandId, brand } = useTenant();
  const theme = activeBrandId;
  const brandName = 'OrgSets';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawRedirect = searchParams.get('redirect_url');
  const redirectUrl = (rawRedirect && rawRedirect.startsWith('/') && !rawRedirect.startsWith('//')) ? rawRedirect : undefined;

  // @ts-expect-error -- window.__AUTH_STATE is an app-level global (written in AuthContext)
  const loadingReasons = window.__AUTH_STATE?.loadingReasons || [];
  const isStalledOnClerk = loadingReasons.includes('!isAuthLoaded') || loadingReasons.includes('!isUserLoaded');
  const isStalledOnSync = loadingReasons.includes('isSyncing') || loadingReasons.includes('needsSync');

  useEffect(() => {
    console.log(`[SignInPage] Render state: isAuthLoading=${isAuthLoading}, isSignedIn=${isSignedIn}, isMaintenanceMode=${isMaintenanceMode}`);
  }, [isAuthLoading, isSignedIn, isMaintenanceMode]);

  useEffect(() => {
    // Avoid redirects during prerendering as it destroys the execution context
    const isPrerendering = (window as any).__PRERENDER_INJECTED === true || 
                          navigator.userAgent.includes('HeadlessChrome');
                          
    if (!isAuthLoading && !isPrerendering && !isMaintenanceMode) {
      if (sessionStorage.getItem('auth_401_bounce')) {
        sessionStorage.removeItem('auth_401_bounce');
        toast.error('Session expired or unauthorized. Please sign in again.');
        if (isSignedIn && (window as any).Clerk) {
          (window as any).Clerk.signOut().catch(() => {});
        }
      } else if (sessionStorage.getItem('logout_reason') === 'idle') {
        sessionStorage.removeItem('logout_reason');
        toast('Signed out due to inactivity to protect your account.', {
          icon: '⏱️',
          id: 'auth-idle-toast',
          duration: 6000
        });
      } else if (isSignedIn) {
        navigate(redirectUrl || '/dashboard');
      }
    }
  }, [isAuthLoading, isSignedIn, isMaintenanceMode, navigate, redirectUrl]);

  const handleMockLogin = () => {
    if (isMaintenanceMode) {
      toast.error('System is under maintenance. Please try again later.');
      return;
    }
    
    loginAs({
      id: 'user-james',
      firstName: 'James',
      lastName: 'Avery',
      fullName: 'James Avery',
      primaryEmailAddress: { emailAddress: 'james@orgsets.com' },
      imageUrl: 'https://picsum.photos/seed/james/100/100',
      publicMetadata: { role: 'PRESIDENT' },
      username: 'james'
    });
  };

  const handleReset = () => {
    toast.loading('Resetting everything...', { id: 'reset' });
    hardReset();
  };

  return (
    <div className="min-h-screen flex flex-col font-sans transition-colors duration-300 bg-white dark:bg-black text-black dark:text-white">
      <SEO title="Sign In" description="Sign in to your organization dashboard." noindex={true} />
      <PublicHeader />
      
      <main className="flex-1 flex flex-col items-center justify-center p-4 pt-28 pb-24">
        <div className={cn(
          "w-full max-w-6xl min-h-[600px] lg:min-h-[680px] overflow-hidden flex flex-col lg:flex-row",
          "bg-white border-8 border-black dark:border-white shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#ffffff] rounded-none dark:bg-black"
        )}>
      {/* Left Side: Marketing/Friendly Info */}
      <div className="hidden lg:flex lg:w-1/2 p-12 flex-col justify-between relative overflow-hidden bg-black text-white dark:bg-white dark:text-black border-r-8 border-black dark:border-white">
        <div className="absolute top-0 right-0 w-64 h-64 bg-brand-primary opacity-10 blur-3xl -mr-32 -mt-32 rounded-full"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-teal-500 opacity-10 blur-3xl -ml-48 -mb-48 rounded-full"></div>
        
        <div className="relative z-10">
          <Link to="/" className="inline-flex items-center gap-3 mb-20 group">
            {brand.logoUrl ? (
              <img src={brand.logoUrl} alt={brandName} className="h-8 w-8 object-contain bg-slate-900/5 dark:bg-white/90 p-0.5 rounded-sm" referrerPolicy="no-referrer" />
            ) : (
              <div className="p-2 rounded-lg group-hover:bg-blue-400/20 transition-colors bg-white/10 dark:bg-black/10">
                <Users className="w-6 h-6 text-blue-400" />
              </div>
            )}
            <span className="text-2xl font-black font-display uppercase tracking-tighter">{brandName}</span>
          </Link>
          
          <h2 className="text-5xl font-black font-display tracking-tighter leading-tight mb-8 break-words !text-inherit">
            Welcome back <br />
            <span className="text-blue-400 italic">to your home.</span>
          </h2>
          
          <div className="space-y-8 max-w-sm">
            <div className="flex gap-4">
              <div className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center border bg-white/10 border-white/10 dark:bg-black/10 dark:border-black/10">
                <Lock className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h4 className="font-bold mb-1 !text-inherit">Secure Sign In</h4>
                <p className="opacity-60 text-sm leading-relaxed !text-inherit">Your data remains yours. We use industry-standard encryption to keep your community safe.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center border bg-white/10 border-white/10 dark:bg-black/10 dark:border-black/10">
                <Sparkles className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h4 className="font-bold mb-1 !text-inherit">Stay Connected</h4>
                <p className="opacity-60 text-sm leading-relaxed !text-inherit">Pick up right where you left off with your club, team, or organization.</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="relative z-10 pt-20">
          <p className="opacity-40 text-xs font-medium tracking-widest uppercase !text-inherit">Everything in its right place.</p>
        </div>
      </div>

      {/* Right Side: Sign In Form */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="mb-10 lg:hidden text-center">
            <Link to="/" className="inline-flex items-center gap-3 mb-6 justify-center">
              {brand.logoUrl ? (
                <img src={brand.logoUrl} alt={brandName} className="h-8 w-8 object-contain bg-slate-900/5 dark:bg-white/90 p-0.5 rounded-sm" referrerPolicy="no-referrer" />
              ) : (
                <Users className="w-6 h-6 text-brand-primary" />
              )}
              <span className="text-2xl font-black font-display tracking-tighter uppercase text-black dark:text-white">{brandName}</span>
            </Link>
            <h1 className="text-3xl font-black font-display text-navy-900 dark:text-white tracking-tight mb-2">Welcome Back</h1>
            <p className="text-gray-500">Sign in to your organization to continue.</p>
          </div>
          
          <div className="overflow-hidden bg-white dark:bg-black border-[4px] border-black dark:border-white shadow-[8px_8px_0px_#0044FF] dark:shadow-[8px_8px_0px_#FFD600]">
            {isMaintenanceMode && (
              <div className="bg-amber-50 border-b border-amber-100 p-6 flex gap-4">
                <div className="shrink-0 w-10 h-10 bg-amber-100 text-amber-600 rounded-xl flex items-center justify-center">
                  <ShieldAlert size={20} />
                </div>
                <div>
                  <p className="text-sm font-bold text-amber-900">Intermittent Connection</p>
                  <p className="text-xs text-amber-700/80 mt-0.5">
                    Our servers are experiencing higher than normal latency. Logins and data sync may be slow or temporarily fail.
                  </p>
                </div>
              </div>
            )}
            <div className="p-2 relative">
              {SHOULD_USE_MOCKS ? (
                (typeof window !== 'undefined' && !!(window as any).__PRERENDER_INJECTED?.isPrerendering) ? (
                  <div className="p-16 flex flex-col items-center justify-center text-center space-y-4">
                    <div className="w-12 h-12 border-4 border-black dark:border-white border-t-[#0044FF] rounded-full animate-spin mx-auto" />
                    <p className="font-bold text-gray-500">Preparing secure environment...</p>
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <div className="flex flex-col items-center gap-6">
                      <div className="w-16 h-16 bg-brand-primary/10 rounded-2xl flex items-center justify-center">
                        <Lock className="w-8 h-8 text-brand-primary" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-navy-900 dark:text-white mb-2">Demo Access</h3>
                        <p className="text-gray-500 text-sm mb-6">
                          You are currently in demo mode. Click below to sign in with a test account.
                        </p>
                      </div>
                      <button 
                        onClick={handleMockLogin}
                        className="w-full py-4 font-bold transition-all active:scale-[0.98] bg-black text-white dark:bg-white dark:text-black rounded-none border-[4px] border-black dark:border-white shadow-[8px_8px_0px_#0044FF] dark:shadow-[8px_8px_0px_#FFD600] hover:shadow-[4px_4px_0px_#0044FF] hover:translate-x-1 hover:translate-y-1 font-black uppercase tracking-wider"
                      >
                        Sign In as James Avery
                      </button>
                      <p className="text-[10px] text-gray-400 mt-4">
                        Live authentication is disabled (No Key Found).
                      </p>
                    </div>
                  </div>
                )
              ) : (
                  <div className="flex flex-col gap-4">
                    {/* Always keep SignIn mounted to avoid disrupting Clerk flows! */}
                    <div className={isAuthLoading ? "hidden" : "block"}>
                      <SignIn 
                        routing="path"
                        path="/sign-in"
                        signUpUrl={redirectUrl ? `/sign-up?redirect_url=${encodeURIComponent(redirectUrl)}` : "/sign-up"}
                        fallbackRedirectUrl={redirectUrl || "/dashboard"}
                        appearance={appearance}
                      />
                    </div>
                    {isAuthLoading && (
                      <div className="flex flex-col items-center justify-center py-16 px-8 text-center">
                        <div className="w-12 h-12 border-4 border-brand-primary border-t-transparent flex items-center justify-center animate-spin rounded-full mb-6"></div>
                        <p className="font-bold text-navy-900 dark:text-white text-lg">Synchronizing workspace...</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                          {isMaintenanceMode 
                            ? "System maintenance may cause delays" 
                            : "Preparing your dashboard. This should only take a moment."}
                        </p>
                      </div>
                    )}
                  </div>
              )}
            </div>
          </div>
          
          <div className="h-24 flex flex-col justify-center mt-4 text-center px-8">
            <p className="text-sm text-gray-500">
              Don't have an account? <Link to={redirectUrl ? `/sign-up?redirect_url=${encodeURIComponent(redirectUrl)}` : "/sign-up"} className="text-brand-primary font-bold hover:underline">Get Started</Link>
            </p>
            <div className="mt-3 flex items-center justify-center gap-4">
              <button onClick={handleReset} className="text-[10px] uppercase tracking-widest font-bold text-gray-400 hover:text-red-500 transition-colors">
                Trouble Signing In? <span className="underline">Reset Session</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      </div>
    </main>
    <PublicFooter />
  </div>
  );
};
